import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Form, Badge, Button, Modal, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney, fmtDate, STATUS_BADGE } from '../lib/format.js';

export default function InvoicesPage() {
  const auth = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const canCreate = auth.can('invoices', 'create');
  const [status, setStatus] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState('');
  const [newError, setNewError] = useState(null);

  const invoices = useQuery({
    queryKey: ['invoices', status],
    queryFn: () => api.get('/invoices', { params: { limit: 1000, status: status || undefined } }).then(r => r.data.items)
  });
  const customers = useQuery({
    queryKey: ['customers-min'],
    queryFn: () => api.get('/customers', { params: { limit: 1000 } }).then(r => r.data.items)
  });

  const customerById = Object.fromEntries((customers.data || []).map(c => [c.id, c]));

  const totals = (invoices.data || []).reduce((acc, i) => {
    if (i.status === 'paid') acc.paid += i.total_cents;
    else if (i.status === 'overdue') acc.overdue += (i.open_cents || 0);
    else if (i.status === 'issued') acc.open += (i.open_cents || 0);
    return acc;
  }, { paid: 0, open: 0, overdue: 0 });

  const newDraft = useMutation({
    mutationFn: () => api.post('/invoices', { customer_id: newCustomerId }).then(r => r.data),
    onSuccess: (data) => { setShowNew(false); nav(`/invoices/${data.invoice.id}`); },
    onError: (e) => setNewError(e.response?.data?.message || JSON.stringify(e.response?.data) || e.message)
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/invoices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices', status] }),
    onError: (e) => alert(e.response?.data?.message || e.message)
  });

  const exportCsv = async () => {
    try {
      const r = await api.get('/invoices/export.csv', {
        params: { year: new Date().getFullYear() },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `rechnungen-${new Date().getFullYear()}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { console.error(e); }
  };

  return (
    <>
      <PageHeader title="Rechnungen" subtitle="Alle ausgestellten und offenen Rechnungen"
        actions={
          <>
            <Button variant="outline-secondary" onClick={exportCsv}>
              <Icon icon="tabler:download" className="me-1" />CSV-Export
            </Button>
            {canCreate && (
              <Button onClick={() => { setNewCustomerId(''); setNewError(null); setShowNew(true); }}>
                <Icon icon="tabler:plus" className="me-1" />Neue Rechnung
              </Button>
            )}
          </>
        } />

      <div className="d-flex gap-3 mb-3 flex-wrap align-items-end">
        <Form.Group style={{ minWidth: 200 }}>
          <Form.Label className="small text-muted mb-1">Status</Form.Label>
          <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Alle</option>
            <option value="draft">Entwürfe</option>
            <option value="issued">Ausgestellt</option>
            <option value="overdue">Überfällig</option>
            <option value="paid">Bezahlt</option>
            <option value="cancelled">Storniert</option>
          </Form.Select>
        </Form.Group>

        <div className="ms-auto d-flex gap-3">
          <div className="text-end">
            <div className="text-muted small">Offen</div>
            <div className="fw-semibold">{fmtMoney(totals.open)}</div>
          </div>
          <div className="text-end">
            <div className="text-muted small">Überfällig</div>
            <div className="fw-semibold text-warning">{fmtMoney(totals.overdue)}</div>
          </div>
          <div className="text-end">
            <div className="text-muted small">Bezahlt</div>
            <div className="fw-semibold text-success">{fmtMoney(totals.paid)}</div>
          </div>
        </div>
      </div>

      <Card className="card-flat">
        <Card.Body className="p-0">
          {invoices.isLoading && <div className="p-4 text-muted">Lade…</div>}
          {invoices.data && invoices.data.length === 0 && (
            <div className="empty-state">
              <Icon icon="tabler:file-invoice" className="empty-icon" />
              <div>Keine Rechnungen.</div>
            </div>
          )}
          {invoices.data && invoices.data.length > 0 && (
            <Table hover className="mb-0 align-middle">
              <thead><tr>
                <th>Nr.</th><th>Kunde</th><th>Status</th>
                <th>Datum</th><th>Fällig</th>
                <th className="text-end">Brutto</th><th className="text-end">Offen</th><th></th>
              </tr></thead>
              <tbody>
                {invoices.data.map(inv => {
                  const sb = STATUS_BADGE[inv.status] || { variant: 'secondary', label: inv.status };
                  const c = customerById[inv.customer_id];
                  return (
                    <tr key={inv.id}>
                      <td>{inv.invoice_number
                        ? <Link to={`/invoices/${inv.id}`} className="text-decoration-none fw-semibold">{inv.invoice_number}</Link>
                        : <Link to={`/invoices/${inv.id}`} className="text-decoration-none text-muted">Entwurf</Link>}</td>
                      <td>{c
                        ? <Link to={`/customers/${c.id}`} className="text-decoration-none">{c.name}</Link>
                        : <span className="text-muted">—</span>}</td>
                      <td><Badge bg="" className={`badge-soft-${sb.variant}`}>{sb.label}</Badge></td>
                      <td className="small">{inv.issue_date ? fmtDate(inv.issue_date) : '—'}</td>
                      <td className="small">{inv.due_date ? fmtDate(inv.due_date) : '—'}</td>
                      <td className="text-end">{fmtMoney(inv.total_cents, inv.currency)}</td>
                      <td className="text-end">{inv.status === 'paid' ? '—' : fmtMoney(inv.open_cents, inv.currency)}</td>
                      <td className="text-end pe-3">
                        <Button as={Link} to={`/invoices/${inv.id}`} size="sm" variant="outline-secondary" className="me-1" title="Öffnen">
                          <Icon icon="tabler:eye" />
                        </Button>
                        {inv.status === 'draft' && (
                          <Button size="sm" variant="outline-danger" title="Entwurf löschen"
                            onClick={() => confirm('Rechnungs-Entwurf löschen?') && del.mutate(inv.id)}>
                            <Icon icon="tabler:trash" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showNew} onHide={() => setShowNew(false)} centered>
        <Modal.Header closeButton><Modal.Title>Neue Rechnung</Modal.Title></Modal.Header>
        <Modal.Body>
          {newError && <Alert variant="danger" className="py-2 small">{newError}</Alert>}
          <Form.Group>
            <Form.Label>Kunde *</Form.Label>
            <Form.Select value={newCustomerId} onChange={(e) => setNewCustomerId(e.target.value)}>
              <option value="">— wählen —</option>
              {(customers.data || []).map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.customer_number ? ` (${c.customer_number})` : ''}
                </option>
              ))}
            </Form.Select>
            <Form.Text>Du wirst direkt zur Rechnungs-Detailseite geleitet, wo du Positionen hinzufügst und die Rechnung ausstellst.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowNew(false)}>Abbrechen</Button>
          <Button onClick={() => newDraft.mutate()} disabled={!newCustomerId || newDraft.isLoading}>
            {newDraft.isLoading ? 'Erstelle…' : 'Entwurf anlegen'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
